<script>
  import { onMount } from 'svelte';
  import api from '../../lib/api.js';

  let activeTab = 'user-roles'; // 'user-roles', 'user-orgs', 'orgs'
  let loading = false;
  let error = null;
  let successMessage = null;

  // Data stores
  let roles = [];
  let orgs = [];
  let userRoles = [];
  let userOrgs = [];

  // Modal state
  let showModal = false;
  let modalMode = 'create'; // 'create' or 'edit'
  let modalType = ''; // 'user-role', 'user-org', 'org'
  let editingItem = null;

  // Form data
  let formUserId = '';
  let formRoleId = 3; // Default to Client
  let formOrgId = '';
  let formOrgName = '';
  let formOrgDescription = '';
  let formIsActive = true;

  // User Organizations tab - new state
  let selectedUserId = null;

  onMount(async () => {
    await loadData();
  });

  async function loadData() {
    loading = true;
    error = null;
    try {
      const [rolesData, orgsData, userRolesData, userOrgsData] = await Promise.all([
        api.getRoles(),
        api.getOrgs(),
        api.getUserRoles(),
        api.getUserOrgs()
      ]);

      roles = rolesData || [];
      orgs = orgsData || [];
      userRoles = userRolesData || [];
      userOrgs = userOrgsData || [];
    } catch (err) {
      console.error('Error loading data:', err);
      error = 'Failed to load data. Please try again.';
    } finally {
      loading = false;
    }
  }

  function openCreateModal(type) {
    modalType = type;
    modalMode = 'create';
    resetForm();
    showModal = true;
  }

  function openEditModal(type, item) {
    modalType = type;
    modalMode = 'edit';
    editingItem = item;

    if (type === 'user-role') {
      formUserId = item.userId;
      formRoleId = item.roleId;
    } else if (type === 'user-org') {
      formUserId = item.userId;
      formOrgId = item.orgId;
    } else if (type === 'org') {
      formOrgId = item.orgId;
      formOrgName = item.orgName;
      formOrgDescription = item.description || '';
      formIsActive = item.isActive;
    }

    showModal = true;
  }

  function resetForm() {
    formUserId = '';
    formRoleId = 3;
    formOrgId = '';
    formOrgName = '';
    formOrgDescription = '';
    formIsActive = true;
    editingItem = null;
  }

  function closeModal() {
    showModal = false;
    resetForm();
  }

  async function handleSubmit() {
    error = null;
    successMessage = null;
    loading = true;

    try {
      if (modalType === 'user-role') {
        const data = { userId: formUserId, roleId: formRoleId };

        if (modalMode === 'create') {
          await api.createUserRole(data);
          successMessage = 'User role created successfully';
        } else {
          await api.updateUserRole(formUserId, data);
          successMessage = 'User role updated successfully';
        }
      } else if (modalType === 'user-org') {
        const data = { userId: formUserId, orgId: formOrgId, isDefault: false };

        if (modalMode === 'create') {
          await api.createUserOrg(data);
          successMessage = 'User assigned to organization successfully';
        }
      } else if (modalType === 'org') {
        const data = {
          orgId: formOrgId,
          orgName: formOrgName,
          description: formOrgDescription,
          isActive: formIsActive
        };

        await api.updateOrg(formOrgId, data);
        successMessage = 'Organization updated successfully';
      }

      await loadData();
      closeModal();

      setTimeout(() => {
        successMessage = null;
      }, 3000);
    } catch (err) {
      console.error('Error saving:', err);
      error = err.message || 'Failed to save. Please try again.';
    } finally {
      loading = false;
    }
  }

  async function handleDelete(type, id1, id2 = null) {
    if (!confirm('Are you sure you want to delete this item?')) {
      return;
    }

    loading = true;
    error = null;

    try {
      if (type === 'user-role') {
        await api.deleteUserRole(id1);
        successMessage = 'User role deleted successfully';
      } else if (type === 'user-org') {
        await api.deleteUserOrg(id1, id2);
        successMessage = 'User removed from organization successfully';
      }

      await loadData();

      setTimeout(() => {
        successMessage = null;
      }, 3000);
    } catch (err) {
      console.error('Error deleting:', err);
      error = err.message || 'Failed to delete. Please try again.';
    } finally {
      loading = false;
    }
  }

  function getRoleName(roleId) {
    const role = roles.find(r => r.roleId === roleId);
    return role ? role.roleName : 'Unknown';
  }

  function getOrgName(orgId) {
    const org = orgs.find(o => o.orgId === orgId);
    return org ? org.orgName : orgId;
  }

  function getUserOrgsForUser(userId) {
    return userOrgs.filter(uo => uo.userId === userId);
  }

  // User Organizations tab - new functions
  function getUniqueUsers() {
    // Get unique users from userRoles (which includes all users)
    const uniqueUserIds = [...new Set(userRoles.map(ur => ur.userId))];
    return uniqueUserIds.map(userId => {
      const userRole = userRoles.find(ur => ur.userId === userId);
      return {
        userId: userId,
        userName: userRole?.user?.orgName || userId
      };
    }).sort((a, b) => a.userName.localeCompare(b.userName));
  }

  function selectUser(userId) {
    selectedUserId = userId;
  }

  function getUserAssignedOrgs(userId) {
    return userOrgs.filter(uo => uo.userId === userId);
  }

  function getAvailableOrgsForUser(userId) {
    const assignedOrgIds = getUserAssignedOrgs(userId).map(uo => uo.orgId);
    return orgs.filter(o => !assignedOrgIds.includes(o.orgId));
  }

  async function assignOrgToUser(orgId) {
    if (!selectedUserId) return;

    loading = true;
    error = null;

    try {
      await api.createUserOrg({
        userId: selectedUserId,
        orgId: orgId,
        isDefault: false
      });

      successMessage = 'Organization assigned successfully';

      // Reload data to refresh the UI
      await loadData();

      setTimeout(() => {
        successMessage = null;
      }, 3000);
    } catch (err) {
      console.error('Error assigning organization:', err);
      error = err.message || 'Failed to assign organization. Please try again.';
    } finally {
      loading = false;
    }
  }

  async function unassignOrgFromUser(orgId) {
    if (!selectedUserId) return;

    loading = true;
    error = null;

    try {
      await api.deleteUserOrg(selectedUserId, orgId);

      successMessage = 'Organization unassigned successfully';

      // Reload data to refresh the UI
      await loadData();

      setTimeout(() => {
        successMessage = null;
      }, 3000);
    } catch (err) {
      console.error('Error unassigning organization:', err);
      error = err.message || 'Failed to unassign organization. Please try again.';
    } finally {
      loading = false;
    }
  }

  function canUnassignOrg(userOrg) {
    // Cannot unassign if it's the user's default org (user's own org where userId === orgId)
    return !userOrg.isDefault;
  }

  function getUserOrgName(userId) {
    const userRole = userRoles.find(ur => ur.userId === userId);
    return userRole?.user?.orgName || userId;
  }

  function getUserEmail(userId) {
    // Find the user's org (where orgId === userId) to get their email
    const userOrg = orgs.find(o => o.orgId === userId);
    return userOrg?.email || '';
  }

</script>

<div class="user-management">
  <div class="header">
    <h1>User Management</h1>
    {#if successMessage}
      <div class="success-message">{successMessage}</div>
    {/if}
    {#if error}
      <div class="error-message">{error}</div>
    {/if}
  </div>

  <div class="tabs">
    <button
      class="tab"
      class:active={activeTab === 'user-roles'}
      on:click={() => activeTab = 'user-roles'}
    >
      User Roles
    </button>
    <button
      class="tab"
      class:active={activeTab === 'user-orgs'}
      on:click={() => activeTab = 'user-orgs'}
    >
      User Organizations
    </button>
    <button
      class="tab"
      class:active={activeTab === 'orgs'}
      on:click={() => activeTab = 'orgs'}
    >
      Organizations
    </button>
  </div>

  <div class="content">
    {#if loading && !showModal}
      <div class="loading">Loading...</div>
    {:else if activeTab === 'user-roles'}
      <div class="tab-content">
        <table class="data-table">
          <thead>
            <tr>
              <th>User ID</th>
              <th>Email</th>
              <th>User/Org Name</th>
              <th>Role</th>
              <th>Assigned At</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {#each userRoles as userRole}
              <tr>
                <td>{userRole.userId}</td>
                <td class="email-cell">{getUserEmail(userRole.userId) || '-'}</td>
                <td>{userRole.user?.orgName || userRole.userId}</td>
                <td>
                  <span class="role-badge role-{userRole.role?.roleName?.toLowerCase()}">
                    {userRole.role?.roleName || getRoleName(userRole.roleId)}
                  </span>
                </td>
                <td>{new Date(userRole.assignedAt).toLocaleDateString()}</td>
                <td class="actions">
                  <button class="btn-edit" on:click={() => openEditModal('user-role', userRole)}>
                    Change Role
                  </button>
                </td>
              </tr>
            {/each}
          </tbody>
        </table>
      </div>
    {:else if activeTab === 'user-orgs'}
      <div class="tab-content user-orgs-redesign">
        <div class="two-column-layout">
          <!-- Left column: User list -->
          <div class="user-list-panel">
            <h3>Select User</h3>
            <div class="user-list">
              {#each getUniqueUsers() as user}
                <div
                  class="user-item"
                  class:selected={selectedUserId === user.userId}
                  on:click={() => selectUser(user.userId)}
                >
                  <div class="user-name">{user.userName}</div>
                  <div class="user-id">{user.userId}</div>
                  {#if getUserEmail(user.userId)}
                    <div class="user-email">{getUserEmail(user.userId)}</div>
                  {/if}
                </div>
              {/each}
            </div>
          </div>

          <!-- Right column: Organization assignments -->
          <div class="org-assignments-panel">
            {#if selectedUserId}
              <div class="panel-header">
                <h3>Organizations for {getUserOrgName(selectedUserId)}</h3>
              </div>

              <!-- Assigned Organizations -->
              <div class="org-section">
                <h4>Assigned Organizations</h4>
                <div class="org-list">
                  {#each getUserAssignedOrgs(selectedUserId) as userOrg}
                    <div class="org-item">
                      <div class="org-info">
                        <div class="org-name">{userOrg.organization?.orgName || getOrgName(userOrg.orgId)}</div>
                        {#if userOrg.isDefault}
                          <span class="badge-default">Default Org</span>
                        {/if}
                      </div>
                      {#if canUnassignOrg(userOrg)}
                        <button
                          class="btn-remove"
                          on:click={() => unassignOrgFromUser(userOrg.orgId)}
                          disabled={loading}
                        >
                          Unassign
                        </button>
                      {:else}
                        <span class="text-muted">Cannot unassign</span>
                      {/if}
                    </div>
                  {/each}
                </div>
              </div>

              <!-- Available Organizations -->
              {@const availableOrgs = getAvailableOrgsForUser(selectedUserId)}
              {#if availableOrgs.length > 0}
                <div class="org-section">
                  <h4>Available Organizations</h4>
                  <div class="org-list">
                    {#each availableOrgs as org}
                      <div class="org-item">
                        <div class="org-info">
                          <div class="org-name">{org.orgName}</div>
                        </div>
                        <button
                          class="btn-add"
                          on:click={() => assignOrgToUser(org.orgId)}
                          disabled={loading}
                        >
                          Assign
                        </button>
                      </div>
                    {/each}
                  </div>
                </div>
              {/if}
            {:else}
              <div class="empty-state">
                <p>Select a user from the list to manage their organization assignments</p>
              </div>
            {/if}
          </div>
        </div>
      </div>
    {:else if activeTab === 'orgs'}
      <div class="tab-content">
        <table class="data-table">
          <thead>
            <tr>
              <th>Organization ID</th>
              <th>Email</th>
              <th>Organization Name</th>
              <th>Description</th>
              <th>Status</th>
              <th>Created At</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {#each orgs as org}
              <tr>
                <td>{org.orgId}</td>
                <td class="email-cell">{org.email || '-'}</td>
                <td>{org.orgName}</td>
                <td>{org.description || '-'}</td>
                <td>
                  {#if org.isActive}
                    <span class="badge-active">Active</span>
                  {:else}
                    <span class="badge-inactive">Inactive</span>
                  {/if}
                </td>
                <td>{new Date(org.createdAt).toLocaleDateString()}</td>
                <td class="actions">
                  <button class="btn-edit" on:click={() => openEditModal('org', org)}>
                    Edit
                  </button>
                </td>
              </tr>
            {/each}
          </tbody>
        </table>
      </div>
    {/if}
  </div>
</div>

{#if showModal}
  <div class="modal-overlay" on:click={(e) => { if (e.target === e.currentTarget) closeModal(); }}>
    <div class="modal">
      <div class="modal-header">
        <h2>
          {#if modalMode === 'create'}
            {#if modalType === 'user-role'}
              Add User Role
            {:else if modalType === 'user-org'}
              Assign User to Organization
            {/if}
          {:else}
            {#if modalType === 'user-role'}
              Edit User Role
            {:else if modalType === 'org'}
              Edit Organization
            {/if}
          {/if}
        </h2>
        <button class="close-btn" on:click={closeModal}>&times;</button>
      </div>

      <form on:submit|preventDefault={handleSubmit}>
        <div class="modal-body">
          {#if modalType === 'user-role'}
            <div class="form-group">
              <label for="userId">User ID / Email</label>
              <input
                id="userId"
                type="text"
                bind:value={formUserId}
                disabled={modalMode === 'edit'}
                required
                placeholder="user@example.com"
              />
            </div>

            <div class="form-group">
              <label for="roleId">Role</label>
              <select id="roleId" bind:value={formRoleId} required>
                {#each roles as role}
                  <option value={role.roleId}>{role.roleName}</option>
                {/each}
              </select>
            </div>
          {:else if modalType === 'user-org'}
            <div class="form-group">
              <label for="userId">User ID / Email</label>
              <input
                id="userId"
                type="text"
                bind:value={formUserId}
                required
                placeholder="user@example.com"
              />
            </div>

            <div class="form-group">
              <label for="orgId">Organization</label>
              <select id="orgId" bind:value={formOrgId} required>
                <option value="">Select organization...</option>
                {#each orgs as org}
                  <option value={org.orgId}>{org.orgName} ({org.orgId})</option>
                {/each}
              </select>
            </div>
          {:else if modalType === 'org'}
            <div class="form-group">
              <label for="orgId">Organization ID</label>
              <input
                id="orgId"
                type="text"
                bind:value={formOrgId}
                disabled
              />
            </div>

            <div class="form-group">
              <label for="orgName">Organization Name</label>
              <input
                id="orgName"
                type="text"
                bind:value={formOrgName}
                required
                placeholder="Organization name"
              />
            </div>

            <div class="form-group">
              <label for="orgDescription">Description</label>
              <textarea
                id="orgDescription"
                bind:value={formOrgDescription}
                rows="3"
                placeholder="Optional description"
              ></textarea>
            </div>

            <div class="form-group">
              <label class="checkbox-label">
                <input
                  type="checkbox"
                  bind:checked={formIsActive}
                />
                Active
              </label>
            </div>
          {/if}
        </div>

        <div class="modal-footer">
          <button type="button" class="btn-secondary" on:click={closeModal}>
            Cancel
          </button>
          <button type="submit" class="btn-primary" disabled={loading}>
            {loading ? 'Saving...' : 'Save'}
          </button>
        </div>
      </form>
    </div>
  </div>
{/if}

<style>
  @import './UserManagement.css';
</style>
